import { motion } from "framer-motion";
import { Mail, Phone } from "lucide-react";
import { FOUNDERS, founderName } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

const Team = () => {
  const { t, lang } = useLang();
  const T = t.team;

  return (
    <section
      id="team"
      data-testid="team-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="container-vel">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="overline mb-5" data-testid="team-overline">
              {T.overline}
            </div>
            <h2
              data-testid="team-heading"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl text-navy font-light leading-[1.05] tracking-tight"
            >
              {T.headline}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 font-body text-base sm:text-lg text-ink-muted leading-relaxed font-light"
          >
            {T.intro}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {FOUNDERS.map((person, idx) => {
            const name = founderName(person, lang);
            const role = T.roles[person.id];
            const bioLong = T.bios[person.id];
            return (
              <motion.article
                key={person.id}
                data-testid={`founder-card-${person.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: idx * 0.1 }}
                className="grid grid-cols-12 gap-6"
              >
                <div className="col-span-12 sm:col-span-5">
                  <div className="relative aspect-[4/5] w-full overflow-hidden bg-navy-50">
                    <img
                      data-testid={`founder-image-${person.id}`}
                      src={person.image}
                      alt={name}
                      className={
                        person.imageClass ||
                        "w-full h-full object-cover grayscale-[15%] hover:grayscale-0 transition-all duration-700"
                      }
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-coral" />
                  </div>
                </div>

                <div className="col-span-12 sm:col-span-7">
                  <div
                    className="overline mb-3"
                    data-testid={`founder-role-${person.id}`}
                  >
                    {role}
                  </div>
                  <h3
                    data-testid={`founder-name-${person.id}`}
                    className="font-heading text-3xl sm:text-4xl text-navy font-medium tracking-tight"
                  >
                    {name}
                  </h3>
                  <div className="mt-5 space-y-4">
                    {bioLong.map((p, i) => (
                      <p
                        key={i}
                        className="font-body text-sm text-ink-muted leading-relaxed font-light"
                      >
                        {p}
                      </p>
                    ))}
                  </div>

                  <div className="mt-7 border-t border-navy/15 pt-5 flex flex-col gap-3">
                    <a
                      data-testid={`founder-email-${person.id}`}
                      href={`mailto:${person.email}`}
                      className="group inline-flex items-center gap-3 font-body text-sm text-navy hover:text-coral transition-colors duration-300"
                    >
                      <Mail size={16} strokeWidth={1.5} />
                      <span className="link-underline">{person.email}</span>
                    </a>
                    <a
                      data-testid={`founder-phone-${person.id}`}
                      href={`tel:${person.phoneHref}`}
                      className="group inline-flex items-center gap-3 font-body text-sm text-navy hover:text-coral transition-colors duration-300"
                    >
                      <Phone size={16} strokeWidth={1.5} />
                      <span className="link-underline">{person.phone}</span>
                    </a>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Team;
